import moment from 'moment';

// `availability` ({summary, positions}, mặc định cả hai true = như prod) cho biết nguồn nào đã lỗi
// (xem chartAvailability). Nguồn lỗi thì không bịa số 0: bỏ điểm biên của summary, và để null
// chuỗi Km khi positions lỗi (Km tính từ gốc odometer của positions). Không có tham số này thì
// hành vi y như cũ.
export const buildChartFromEvents = (
  events,
  summaryRows,
  positions,
  oilRows,
  firstTime,
  lastTime,
  availability
) => {
  const summaryOk = availability ? availability.summary !== false : true;
  const positionsOk = availability ? availability.positions !== false : true;
  const kmOf = v => (positionsOk ? v : null);
  const isMultiDay = moment.unix(firstTime).format('DD/MM/YYYY') !== moment.unix(lastTime).format('DD/MM/YYYY');
  const formatStr = isMultiDay ? 'HH:mm DD/MM' : 'HH:mm';

  const firstRow = summaryRows[0] || {};
  const startLevel = Number(firstRow.startLevel || firstRow.StartLevel || firstRow.start_level || 0);
  const firstKm = positions && positions.length > 0 ? Number(positions[0].distance ?? positions[0].Distance ?? positions[0].kmAccum ?? positions[0].KmAccum ?? positions[0].km_accum ?? 0) : 0;

  const eventPoints = [...events]
    .sort((a, b) => moment(a.time, 'HH:mm DD/MM/YYYY') - moment(b.time, 'HH:mm DD/MM/YYYY'))
    .map(e => ({ time: moment(e.time, 'HH:mm DD/MM/YYYY').unix(), type: 'event', data: e }));

  let speedPoints = [];
  if (positions && positions.length > 0) {
    const step = Math.max(1, Math.ceil(positions.length / 150));
    speedPoints = positions
      .filter((_, i) => i % step === 0)
      .map(p => ({ time: p.created_at, type: 'speed', data: p }));
  }

  let oilPoints = [];
  if (oilRows && oilRows.length > 0) {
    const step = Math.max(1, Math.ceil(oilRows.length / 150));
    oilPoints = oilRows
      .filter((_, i) => i % step === 0)
      .map(o => ({ time: o["100"], type: 'oil', data: o }));
  }

  const allPoints = [...speedPoints, ...eventPoints, ...oilPoints].sort((a, b) => a.time - b.time);

  const labels = [];
  const fuelData = [];
  const kmData = [];
  const speedData = [];
  const rawPoints = [];

  if (summaryOk) {
    labels.push(moment.unix(firstTime).format(formatStr));
    fuelData.push(parseFloat(startLevel.toFixed(2)));
    kmData.push(kmOf(0));
    speedData.push(null);
    rawPoints.push({ time: firstTime, fuel: startLevel, Km: kmOf(0), speed: null, address: null, engineStatus: null, type: 'boundary' });
  }

  let currentFuel = summaryOk ? startLevel : null;
  let currentKm = 0;
  let currentAddress = null;
  let currentEngineStatus = null;

  allPoints.forEach(pt => {
    const tLabel = moment.unix(pt.time).format(formatStr);

    if (pt.type === 'event') {
      const e = pt.data;
      const levelBefore = parseFloat(Number(e.levelBefore || e.LevelBefore || e.level_before || 0).toFixed(2));
      const levelAfter = parseFloat(Number(e.levelAfter || e.LevelAfter || e.level_after || 0).toFixed(2));
      const Km = parseFloat(Number(e.distance ?? e.Distance ?? e.kmAccum ?? e.KmAccum ?? e.km_accum ?? 0).toFixed(2));
      const addr = e.address || e.Address || e.addr || currentAddress || null;
      const evType = levelAfter >= levelBefore ? 'fill' : 'drain';

      currentAddress = addr;

      labels.push(tLabel); fuelData.push(levelBefore); kmData.push(kmOf(Km - firstKm)); speedData.push(null);
      rawPoints.push({ time: pt.time, fuel: levelBefore, Km: kmOf(Km - firstKm), speed: null, address: addr, engineStatus: null, type: evType });
      labels.push(tLabel + "'"); fuelData.push(levelAfter); kmData.push(kmOf(Km - firstKm)); speedData.push(null);
      rawPoints.push({ time: pt.time, fuel: levelAfter, Km: kmOf(Km - firstKm), speed: null, address: addr, engineStatus: null, type: evType });

      currentFuel = levelAfter;
      currentKm = Km;
    } else if (pt.type === 'speed') {
      const e = pt.data;

      const fuelRaw = (oilRows && oilRows.length > 0) ? currentFuel : (e.levelFuel || e.LevelFuel || e.level_fuel || currentFuel);
      const fuel = fuelRaw == null ? (summaryOk ? 0 : null) : fuelRaw;
      const Km = e.distance ?? e.Distance ?? e.kmAccum ?? e.KmAccum ?? e.km_accum ?? currentKm ?? 0;
      const spd = e.speedGps != null ? Math.round(e.speedGps) : null;
      const addr = e.address || e.Address || e.addr || currentAddress || null;
      const eng = e.engineStatus != null ? e.engineStatus : currentEngineStatus;

      currentFuel = fuel == null ? null : parseFloat(Number(fuel).toFixed(2));
      currentKm = Km;
      currentAddress = addr;
      currentEngineStatus = eng;

      labels.push(tLabel);
      fuelData.push(currentFuel);
      kmData.push(kmOf(parseFloat((currentKm - firstKm).toFixed(2))));
      speedData.push(spd);
      rawPoints.push({
        time: pt.time,
        fuel: currentFuel,
        Km: kmOf(parseFloat((currentKm - firstKm).toFixed(2))),
        speed: e.speedGps,
        address: addr,
        engineStatus: eng,
        type: 'speed',
      });
    } else if (pt.type === 'oil') {
      const o = pt.data;
      const fuelValue = parseFloat(Number(o.oil_cap || 0).toFixed(2));
      currentFuel = fuelValue;

      labels.push(tLabel);
      fuelData.push(currentFuel);
      kmData.push(kmOf(parseFloat((currentKm - firstKm).toFixed(2))));
      speedData.push(null);
      rawPoints.push({
        time: pt.time,
        fuel: currentFuel,
        Km: kmOf(parseFloat((currentKm - firstKm).toFixed(2))),
        speed: null,
        address: currentAddress,
        engineStatus: currentEngineStatus,
        type: 'oil',
      });
    }
  });

  if (summaryOk) {
    const lastRow = summaryRows[summaryRows.length - 1];
    const totalKm = summaryRows.reduce((s, r) => s + Number(r.totalKmGPS || r.TotalKmGPS || r.total_km_gps || 0), 0);
    const endLevel = lastRow ? Number(lastRow.endLevel || lastRow.EndLevel || lastRow.end_level || 0) : startLevel;
    labels.push(moment.unix(lastTime).format(formatStr));
    fuelData.push(parseFloat(endLevel.toFixed(2)));
    kmData.push(parseFloat(totalKm.toFixed(2)));
    speedData.push(null);
    rawPoints.push({ time: lastTime, fuel: endLevel, Km: totalKm, speed: null, address: null, engineStatus: null, type: 'boundary' });
  }

  return { labels, fuelData, kmData, speedData, rawPoints };
};
